import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useLayoutEffect,
} from 'react'

export interface LayeredContextValue<T> {
  /**
   * The current value.
   */
  value: T

  /**
   * Adds an entry with the given priority and returns a function to remove it.
   *
   * @param value
   * @param priority
   */
  add(value: T, priority?: number): () => void
}

export interface LayeredContextOptions {
  /**
   * If `true`, excludes `null` values when evaluating which entry to use.
   */
  excludeNulls?: boolean
}

/**
 * Creates a layered context.
 *
 * A layered context allows multiple child components to provide a
 * value to sibling or parent components.
 *
 * The value with the highest priority is what is returned to all consumers.
 *
 * @param defaultValue
 * @param options
 */
export function createLayeredContext<T>(
  defaultValue: T,
  options: LayeredContextOptions = {},
) {
  const Context = createContext<LayeredContextValue<T>>({
    value: defaultValue,
    add: () => () => undefined,
  })

  /**
   * Provider component
   * @param children
   */
  function Provider({ children }: { children: ReactNode }) {
    interface Entry {
      value: T
      priority: number
    }

    const [entries, setEntries] = useState<Entry[]>([])

    const add = useCallback((value: T, priority = 0) => {
      const entry: Entry = { value, priority }
      setEntries((entries) =>
        // Add the entry and sort based on priority - higher priority means
        // the value takes precedence.
        [...entries, entry].sort((a, b) => b.priority - a.priority),
      )
      return () => {
        setEntries((entries) => entries.filter((e) => e !== entry))
      }
    }, [])

    const ctx = useMemo((): LayeredContextValue<T> => {
      const value = entries.length === 0 ? defaultValue : entries[0].value
      return {
        value,
        add,
      }
    }, [add, entries])

    return <Context.Provider value={ctx}>{children}</Context.Provider>
  }

  /**
   * Value provider hook
   *
   * @param value
   * @param priority
   */
  function useProvide(value: T, priority?: number) {
    const { add } = useContext(Context)
    useLayoutEffect(() => {
      if (value === null && options.excludeNulls) {
        return
      }

      return add(value, priority)
    }, [add, value, priority])
  }

  /**
   * Value reader hook.
   */
  function useValue() {
    return useContext(Context).value
  }

  return {
    Provider,
    Consumer: Context.Consumer,
    useProvide,
    useValue,
  }
}
